import React, { useState } from 'react';
import { Modal, Typography, Button } from '@crisellevaldez/speculo-ui';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, Calendar } from 'lucide-react';
import { useClientOwner } from '../../../hooks/useClientOwner';

interface TourCompleteModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Optional contact ID to navigate to the specific contact page */
  contactId?: string | null;
  /** Whether the tour was skipped (shows different content) */
  wasSkipped?: boolean;
}

export const TourCompleteModal: React.FC<TourCompleteModalProps> = ({
  isOpen,
  onClose,
  contactId,
  wasSkipped = false,
}) => {
  const navigate = useNavigate();
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const owner = useClientOwner();
  const strategyCallEmbedUrl = `${owner.strategyMeetingUrl}?embed=true`;

  // Determine if this was a completion without test call (Twilio not configured)
  // wasSkipped=false but no contactId means tour completed early without test call
  const completedWithoutTestCall = !wasSkipped && !contactId;

  // Get appropriate message based on completion type
  const getDescription = () => {
    if (wasSkipped) {
      return "Congratulations! You've successfully completed the onboarding. Feel free to navigate the platform and explore all the features available to you.";
    }
    if (completedWithoutTestCall) {
      return "Congratulations! You've successfully completed the Flow guide. Your phone system is being set up by our team - once complete, you'll be able to make test calls. Feel free to explore the platform!";
    }
    return "Congratulations! You've successfully completed the Flow guide. Your test call has been initiated. After the call completes, you can view the conversation transcript on the Contacts page.";
  };

  // Get appropriate button text and action
  const getButtonConfig = () => {
    if (wasSkipped || completedWithoutTestCall) {
      return {
        text: 'Go to Dashboard',
        action: () => {
          onClose();
          navigate('/dashboard');
        },
      };
    }
    return {
      text: 'View Contact',
      action: () => {
        onClose();
        if (contactId) {
          // Open contact page in a new tab
          window.open(`/contact/${contactId}`, '_blank');
        } else {
          // Open contacts page in a new tab
          window.open('/contacts', '_blank');
        }
      },
    };
  };

  const buttonConfig = getButtonConfig();

  const handleScheduleClick = () => {
    setShowScheduleModal(true);
  };

  const handleCloseScheduleModal = () => {
    setShowScheduleModal(false);
  };

  return (
    <>
      <Modal isOpen={isOpen && !showScheduleModal} onClose={onClose}>
        <div className="text-center py-6 px-4">
          {/* Success Icon */}
          <div className="mx-auto w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mb-4">
            <CheckCircle className="w-8 h-8 text-green-600 dark:text-green-400" />
          </div>

          {/* Title */}
          <Typography variant="h2" className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2">
            🎉 Onboarding Complete!
          </Typography>

          {/* Description */}
          <Typography variant="p" className="text-gray-600 dark:text-gray-400 mb-6">
            {getDescription()}
          </Typography>

          {/* Schedule Strategy Call Button */}
          <Button
            variant="primary"
            size="lg"
            className="w-full mb-4 flex items-center justify-center gap-2"
            onClick={handleScheduleClick}
          >
            <Calendar className="w-5 h-5" />
            Schedule Strategy Call
          </Button>

          {/* Actions */}
          <div className="flex gap-3">
            <Button variant="outline" size="lg" className="flex-1" onClick={onClose}>
              Stay
            </Button>
            <Button variant="secondary" size="lg" className="flex-1" onClick={buttonConfig.action}>
              {buttonConfig.text}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Schedule Strategy Call Modal with HubSpot Embed */}
      <Modal isOpen={showScheduleModal} onClose={handleCloseScheduleModal} size="lg">
        <Modal.Header>
          <Typography variant="h3">
            Schedule Your Strategy Call
          </Typography>
        </Modal.Header>
        <Modal.Body className="overflow-hidden">
          <div className="meetings-iframe-container" style={{ minHeight: '600px' }}>
            <iframe
              src={strategyCallEmbedUrl}
              width="100%"
              height="600"
              frameBorder="0"
              title="Schedule Strategy Call"
              style={{ border: 'none' }}
            />
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};
